import { motion } from "framer-motion";
import { Shield, Lock, Server, Eye, FileCheck, Database } from "lucide-react";

const pillars = [
  { icon: Lock, title: "Data Never Leaves", desc: "Client-controlled environments with zero data leakage" },
  { icon: Shield, title: "Compliance Ready", desc: "RBI / DPDP / enterprise compliance by design" },
  { icon: Server, title: "Sovereign Deployment", desc: "On-prem, VPC, or sovereign cloud infrastructure" },
  { icon: Eye, title: "Full Auditability", desc: "Complete governance and audit trail capabilities" },
  { icon: FileCheck, title: "No Model Training", desc: "Client data is never used for model training" },
  { icon: Database, title: "Data Sovereignty", desc: "Data residency within national boundaries" },
];

export const PhilosophySection = () => (
  <section id="philosophy" data-testid="philosophy-section" className="py-24 md:py-32 section-alt relative">
    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[#c9a84c] font-heading font-semibold text-sm tracking-widest uppercase mb-4"
          >
            Core Philosophy
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-tight"
          >
            Private AI as the{" "}
            <span className="text-gradient-gold">Foundation</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="font-body text-muted-foreground mt-6 text-base leading-relaxed"
          >
            Ojas AI is built on a Private AI architecture that ensures complete
            data sovereignty, regulatory compliance, and enterprise-grade trust.
            Privacy isn't an afterthought — it's our core architecture.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="glass-card rounded-lg p-5 group"
              data-testid={`philosophy-pillar-${i}`}
            >
              <p.icon className="h-6 w-6 text-[#c9a84c] mb-3 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="font-heading font-semibold text-foreground text-sm">{p.title}</h3>
              <p className="font-body text-muted-foreground text-xs mt-1 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
